import { getItemName } from '$lib/er/item/translation';
import LL from '$i18n/i18n-svelte';
import type { Locales } from '$i18n/i18n-types';
import flexsearch from 'flexsearch';
import hanguljs from 'hangul-js';
import { get } from 'svelte/store';
import { getTranslatedCharacterName } from '$lib/er/character/translations';
import { api, type ResultOf } from '$lib/erar/api';
import { orderBy } from '$lib/utils/array';
import { idToCharacter } from '$lib/er/character/stores';
import { gradeToNumber, idToItem } from '$lib/er/item/stores';

const Index = flexsearch.Index ?? flexsearch;
const { disassemble: disassembleHangul, assemble: assembleHangul } = hanguljs;

export interface SearchResult {
  players: ResultOf<'GET /search/players'>['result'];
  items: Array<{ id: number; name: string }>;
  // TODO [홈-검색]
  characters: Array<{ id: number; name: string }>;
  // routes: ResultOf<'GET /search/routes'>['result'];
}

const commonIndexOptions = {
  tokenize: (str: string) => {
    // https://github.com/daangn/websites/blob/3fbc1841c38afeaba876b7f0ceae5f84778c6089/_packages/%40karrotmarket/gatsby-theme-website-team/gatsby/gatsby-config.ts#L61
    const specialCharactersRegex = /[{}[]\/?.,;:|\)*~`!^-_+<>@#$%&\\=\('"]/g;
    const splitted = str.replace(specialCharactersRegex, '').trim().split(/\s/);

    const wordSet = new Set([...splitted]);
    const tokens: string[] = [];
    for (const word of wordSet) {
      const syllables = disassembleHangul(word);
      for (let i = 0; i < syllables.length; i++) {
        const token = assembleHangul(syllables.slice(0, i + 1)).toLocaleLowerCase();
        tokens.push(token);
      }
    }

    return tokens;
  },
};

export let itemIndex: flexsearch.Index;
export let characterIndex: flexsearch.Index;

export function initIndexes(locale: Locales) {
  const options: flexsearch.IndexOptions<string, false> = {
    ...commonIndexOptions,
    language: locale,
    encode:
      locale === 'ko' || locale === 'ja'
        ? // eslint-disable-next-line no-control-regex
          (str) => str.replace(/[\x00-\x7F]/g, '').split('')
        : undefined,
  };

  itemIndex = new Index(options);
  characterIndex = new Index(options);
}

export async function search(input: string): Promise<SearchResult> {
  let players: ResultOf<'GET /search/players'>['result'] = [];

  // 최적화: 유저 닉네임에는 공백이 들어갈 수 없다
  if (!input.includes(' ')) {
    const users = await api('GET /search/players', {
      query: { nameQuery: input },
    });
    if (users.result != undefined) {
      players = users.result;
    }
  }

  const { result: routes } = await api('GET /search/routes', { query: { titleQuery: input } });
  routes.sort(orderBy((v) => v.likes, true));

  return {
    players,
    items: searchItem(input, 10, true),
    characters: searchCharacter(input, 10),
    // routes,
  };
}

export function searchItem(
  input: string,
  limit: number,
  suggest: boolean,
): { id: number; name: string }[] {
  if (input === '') {
    const list = [...get(idToItem).values()]
      .sort(orderBy((v) => gradeToNumber[v.gradeId], true))
      .map(({ id }) => ({
        id,
        name: getItemName(get(LL), id),
      }));
    if (limit === -1) {
      return list;
    }
    return list.slice(0, 5);
  }
  const itemResult = itemIndex.search(input, { suggest, limit });
  const items = itemResult.map((id) => {
    const numberId = id as number;
    return { id: numberId, name: getItemName(get(LL), numberId) };
  });

  return items;
}

export function searchCharacter(input: string, limit: number): { id: number; name: string }[] {
  const list = [...get(idToCharacter).values()].map(({ id }) => ({
    id,
    name: getTranslatedCharacterName(get(LL), id),
  }));
  if (input === '') {
    if (limit === -1) return list;
    return list.slice(0, 5);
  }
  const disassembledInput = disassembleHangul(input, true).map((v) => v.join(''));
  return list.filter(({ name }) => {
    if (name.includes(input)) return true;
    const disassembledName = disassembleHangul(name, true);
    if (disassembledInput.length > disassembledName.length) return false;
    for (let i = 0; i < disassembledName.length; ++i)
      if (disassembledInput[i] === undefined) return true;
      else if (!disassembledName[i].join('').includes(disassembledInput[i])) return false;
    return true;
  });
}
